import React, { useState, useMemo, useEffect } from 'react';
import { ChefHat, Plus, Edit, TrendingUp, TrendingDown, Minus, Package, DollarSign, Settings } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Product } from '../types/product.types';
import { ingredientsService } from '@/services/ingredients.service';
import { productsService } from '@/services/products.service';
import { toast } from 'sonner';
import type { Ingredient } from '@/types/ingredient.types';

interface ProductRecipesModalProps {
  isOpen: boolean;
  product: Product | null;
  onClose: () => void;
}

export const ProductRecipesModal: React.FC<ProductRecipesModalProps> = ({
  isOpen,
  product,
  onClose
}) => {
  const [isPresentationManageModalOpen, setIsPresentationManageModalOpen] = useState(false);
  const [selectedPresentation, setSelectedPresentation] = useState<any>(null);
  const [editablePrice, setEditablePrice] = useState<number>(0);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loadingIngredients, setLoadingIngredients] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // New recipe form state
  const [newRecipeForm, setNewRecipeForm] = useState({
    presentation: '',
    ingredients: [
      { ingredientId: '', quantity: '1', notes: '' }
    ]
  });

  // Load ingredients from database
  useEffect(() => {
    const loadIngredients = async () => {
      try {
        setLoadingIngredients(true);
        const response = await ingredientsService.getIngredients({
          includeInactive: false,
          limit: 1000 // Load all ingredients for recipe dropdown
        });
        if (response.success) {
          setIngredients(response.data);
        }
      } catch (error) {
        console.error('Error loading ingredients:', error);
        setIngredients([]);
      } finally {
        setLoadingIngredients(false);
      }
    };

    if (isOpen && ingredients.length === 0) {
      loadIngredients();
    }
  }, [isOpen, ingredients.length]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  // Ingredient management functions
  const handleAddIngredient = () => {
    setNewRecipeForm(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { ingredientId: '', quantity: '1', notes: '' }]
    }));
  };

  const handleRemoveIngredient = (index: number) => {
    setNewRecipeForm(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const handleIngredientChange = (index: number, field: string, value: string) => {
    setNewRecipeForm(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ingredient, i) => 
        i === index ? { ...ingredient, [field]: value } : ingredient
      )
    }));
  };

  const handleFormFieldChange = (field: string, value: string) => {
    setNewRecipeForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Función para cargar receta guardada
  const loadSavedRecipe = async (presentation: any) => {
    try {
      setLoading(true);
      const response = await productsService.getRecipe(presentation.id);
      
      if (response.success && response.data.hasRecipe) {
        // Cargar ingredientes guardados en el formulario
        setNewRecipeForm({
          presentation: presentation.name,
          ingredients: response.data.ingredients.map((ing: any) => ({
            ingredientId: String(ing.ingredientId),
            quantity: ing.quantity.toString(),
            notes: ing.notes || ''
          }))
        });
      } else {
        // Resetear formulario si no hay receta guardada
        setNewRecipeForm({
          presentation: presentation.name,
          ingredients: [{ ingredientId: '', quantity: '1', notes: '' }]
        });
      }
    } catch (error) {
      console.error('Error loading recipe:', error);
      // En caso de error, usar formulario vacío
      setNewRecipeForm({
        presentation: presentation.name,
        ingredients: [{ ingredientId: '', quantity: '1', notes: '' }]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRecipe = async () => {
    if (!product || !selectedPresentation) {
      return;
    }

    // Validate ingredients
    const validIngredients = newRecipeForm.ingredients.filter(
      ing => ing.ingredientId && ing.quantity && parseFloat(ing.quantity) > 0
    );

    if (validIngredients.length === 0) {
      toast.error('Debe agregar al menos un ingrediente válido');
      return;
    }

    try {
      setLoading(true);
      
      // Call API to save recipe
      const response = await productsService.saveRecipe(selectedPresentation.id, validIngredients);
      
      if (response.success) {
        toast.success(response.message || 'Receta guardada exitosamente');
        
        // Reset form
        setNewRecipeForm({
          presentation: '',
          ingredients: [{ ingredientId: '', quantity: '1', notes: '' }]
        });
        setSelectedPresentation(null);
        setIsPresentationManageModalOpen(false);
      } else {
        toast.error('Error al guardar la receta');
      }
    } catch (error) {
      console.error('Error saving recipe:', error);
      toast.error(error instanceof Error ? error.message : 'Error al guardar la receta');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get ingredient unit price
  const getIngredientUnitPrice = (dbIngredient: Ingredient): number => {
    // Try to get price from preferred supplier first
    const preferredSupplier = dbIngredient.suppliers?.find(s => s.isPreferred && s.isActive);
    if (preferredSupplier?.unitPrice) {
      return preferredSupplier.unitPrice;
    }
    // Fallback to any active supplier
    const activeSupplier = dbIngredient.suppliers?.find(s => s.isActive);
    if (activeSupplier?.unitPrice) {
      return activeSupplier.unitPrice;
    }
    // Fallback to globalCostPerUnit
    return dbIngredient.globalCostPerUnit || 0;
  };

  // Calculate ingredients cost dynamically
  const calculateIngredientsCost = useMemo(() => {
    if (!newRecipeForm.ingredients || ingredients.length === 0) return 0;

    return newRecipeForm.ingredients.reduce((total, ingredient) => {
      if (!ingredient.ingredientId || !ingredient.quantity) return total;

      const dbIngredient = ingredients.find(ing => String(ing.id) === String(ingredient.ingredientId));
      if (!dbIngredient) return total;

      const quantity = parseFloat(ingredient.quantity) || 0;
      const unitPrice = getIngredientUnitPrice(dbIngredient);

      return total + (quantity * unitPrice);
    }, 0);
  }, [newRecipeForm.ingredients, ingredients]);

  // Real presentations data from the product
  const productPresentations = useMemo(() => {
    if (!product || !product.presentations) return [];
    
    // Use real presentations from the product
    return product.presentations.map(presentation => ({
      id: presentation.id,
      name: presentation.name,
      description: presentation.description,
      price: presentation.price,
      isActive: presentation.isActive
    }));
  }, [product]);

  if (!product) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] w-[95vw] min-w-[900px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChefHat className="w-5 h-5 text-macartur-primary" />
              Recetario - {product.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Product Presentations Cards */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Selecciona una presentación para configurar su receta</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {productPresentations.map((presentation) => (
                  <div
                    key={presentation.id}
                    className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                      presentation.isActive 
                        ? 'border-gray-200 bg-white' 
                        : 'border-gray-100 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-macartur-primary" />
                        <h4 className="font-medium text-gray-900">{presentation.name}</h4>
                      </div>
                      <Badge 
                        className={presentation.isActive 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-700'
                        }
                      >
                        {presentation.isActive ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-gray-500" />
                        <span className="text-lg font-bold text-gray-900">
                          {formatCurrency(presentation.price)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <ChefHat className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          Receta personalizada
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs text-macartur-primary hover:bg-macartur-primary hover:text-white"
                        onClick={async () => {
                          setSelectedPresentation(presentation);
                          setEditablePrice(presentation.price);
                          setIsPresentationManageModalOpen(true);
                          // Cargar receta guardada al abrir el modal
                          await loadSavedRecipe(presentation);
                        }}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Presentation Management Modal */}
      <Dialog open={isPresentationManageModalOpen} onOpenChange={setIsPresentationManageModalOpen}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] w-[90vw] min-w-[1000px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-macartur-primary" />
              {product?.name} - {selectedPresentation?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Resumen compacto */}
            <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Categoría:</span>
                <Badge className="bg-orange-100 text-orange-700">
                  {product?.category || 'hamburguesas'}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Estado:</span>
                <Badge className={selectedPresentation?.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                  {selectedPresentation?.isActive ? 'Activa' : 'Inactiva'}
                </Badge>
              </div>
              <div className="ml-auto flex items-center gap-6">
                <div className="text-center">
                  <div className="text-xs text-gray-500">Costo</div>
                  <div className="font-bold">{formatCurrency(calculateIngredientsCost)}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500">Precio</div>
                  <div className="font-bold">{formatCurrency(selectedPresentation?.price || 0)}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500">Ganancia</div>
                  <div className="font-bold text-green-600">{formatCurrency((selectedPresentation?.price || 0) - calculateIngredientsCost)}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500">Margen</div>
                  <div className={`font-bold flex items-center gap-1 ${
                    ((selectedPresentation?.price || 0) - calculateIngredientsCost) > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {((selectedPresentation?.price || 0) - calculateIngredientsCost) > 0 ?
                      <TrendingUp className="w-4 h-4" /> :
                      <TrendingDown className="w-4 h-4" />
                    }
                    {selectedPresentation?.price > 0 ?
                      (((((selectedPresentation?.price || 0) - calculateIngredientsCost) / (selectedPresentation?.price || 1)) * 100).toFixed(1) + '%') :
                      '0%'
                    }
                  </div>
                </div>
              </div>
            </div>

            {/* Recipe/Ingredients Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Receta de la Presentación</h3>
              
              {/* Ingredients Section */}
              <div className="border rounded-lg p-4 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-base font-medium text-gray-900">Lista de Ingredientes</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddIngredient}
                    className="text-macartur-primary border-macartur-primary hover:bg-macartur-primary hover:text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Ingrediente
                  </Button>
                </div>

                {newRecipeForm.ingredients.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Ingrediente</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700 w-24">Cantidad</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700 w-32">Unidad</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700 w-24">Precio</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Notas</th>
                          <th className="text-center py-3 px-2 font-medium text-gray-700 w-12">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newRecipeForm.ingredients.map((ingredient, index) => {
                          const dbIngredient = ingredients.find(ing => String(ing.id) === String(ingredient.ingredientId));
                          const unitPrice = dbIngredient ? getIngredientUnitPrice(dbIngredient) : 0;
                          const itemCost = (parseFloat(ingredient.quantity) || 0) * unitPrice;
                          
                          return (
                            <tr key={index} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                              <td className="py-3 px-2">
                                <Select
                                  value={ingredient.ingredientId}
                                  onValueChange={(value) => handleIngredientChange(index, 'ingredientId', value)}
                                  disabled={loadingIngredients}
                                >
                                  <SelectTrigger className="w-full min-w-[200px]">
                                    <SelectValue placeholder={loadingIngredients ? "Cargando..." : "Seleccionar..."} />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-60 overflow-y-auto">
                                    {ingredients.map((dbIngredient) => (
                                      <SelectItem key={dbIngredient.id} value={String(dbIngredient.id)}>
                                        {dbIngredient.name}
                                      </SelectItem>
                                    ))}
                                    {ingredients.length === 0 && !loadingIngredients && (
                                      <SelectItem value="" disabled>
                                        No hay ingredientes disponibles
                                      </SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="py-3 px-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={ingredient.quantity}
                                  onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                                  className="w-full"
                                  placeholder="1"
                                />
                              </td>
                              <td className="py-3 px-2 text-sm text-gray-600">
                                {dbIngredient?.unit || '-'}
                              </td>
                              <td className="py-3 px-2 text-sm font-medium">
                                {formatCurrency(itemCost)}
                              </td>
                              <td className="py-3 px-2">
                                <Input
                                  value={ingredient.notes}
                                  onChange={(e) => handleIngredientChange(index, 'notes', e.target.value)}
                                  placeholder="Ej: al gusto..."
                                  className="w-full min-w-[120px]"
                                />
                              </td>
                              <td className="py-3 px-2 text-center">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveIngredient(index)}
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  disabled={newRecipeForm.ingredients.length === 1}
                                >
                                  <Minus className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="border-t-2 border-gray-300 bg-gray-100">
                          <td colSpan={3} className="py-3 px-2 font-medium text-gray-900">Total:</td>
                          <td className="py-3 px-2 font-bold text-lg">{formatCurrency(calculateIngredientsCost)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {newRecipeForm.ingredients.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <ChefHat className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p>No hay ingredientes agregados</p>
                    <p className="text-sm">Haz clic en "Agregar Ingrediente" para comenzar</p>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedPresentation(null);
                  setIsPresentationManageModalOpen(false);
                }}
              >
                Cerrar
              </Button>
              <Button
                className="bg-macartur-primary hover:bg-macartur-primary/90"
                onClick={handleSaveRecipe}
                disabled={loading}
              >
                {loading ? 'Guardando...' : 'Guardar Receta'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};